/* global globalThis */
import { getPrivateAudioContext, requestPrivateAudio } from "./collectionV2";
const RECEIPT_KEY = "pure_collection_private_audio_receipt_v1";
const uploadKeys = new WeakMap();
export const RAW_AUDIO_CHANGED = "pure-private-audio-changed";

export const getAudioPermission = () => {
    const context = getPrivateAudioContext();
    if (!context) return null;
    try {
        const receipt = JSON.parse(globalThis.localStorage.getItem(RECEIPT_KEY) || "null");
        return receipt?.participant_id === context.participant_id && receipt?.consent_id === context.consent_id
            && /^acn_[0-9a-f]{32}$/.test(receipt?.audio_consent_id || "") ? receipt : null;
    } catch { return null; }
};

export const loadAudioPolicy = async () => {
    const policy = await requestPrivateAudio("policy", { publicRequest: true });
    const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(policy.canonical_text));
    const hash = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, "0")).join("");
    const canonical = JSON.parse(policy.canonical_text);
    if (hash !== policy.policy_sha256 || canonical.notice !== policy.notice || canonical.retention_days !== policy.retention_days || canonical.version !== policy.version) {
        throw new Error("Recording-storage policy could not be verified.");
    }
    return policy;
};

export const recordAudioPermission = async policy => {
    const context = getPrivateAudioContext();
    if (!context || !policy?.enabled) throw new Error("Verified speech consent and configured private recording storage are required.");
    const receipt = await requestPrivateAudio("consents", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...context, policy_sha256: policy.policy_sha256,
            authorize_recording_storage: true, voice_privacy_risks_acknowledged: true }),
    });
    if (getPrivateAudioContext()?.consent_id !== context.consent_id) {
        throw new Error("Research consent changed. Recording storage was not enabled in this browser.");
    }
    if (receipt.participant_id !== context.participant_id || receipt.consent_id !== context.consent_id || receipt.policy_sha256 !== policy.policy_sha256 || !/^acn_[0-9a-f]{32}$/.test(receipt.audio_consent_id || "")) {
        throw new Error("Recording-storage receipt could not be verified.");
    }
    globalThis.localStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt));
    window.dispatchEvent(new Event(RAW_AUDIO_CHANGED));
    return receipt;
};

export const revokeAudioPermission = async receipt => {
    const result = await requestPrivateAudio(`consents/${receipt.audio_consent_id}`, { method: "DELETE" });
    globalThis.localStorage.removeItem(RECEIPT_KEY);
    window.dispatchEvent(new Event(RAW_AUDIO_CHANGED));
    return result;
};

export const savePrivateRecording = async ({ blob, gameId, receipt, session, signal }) => {
    if (getAudioPermission()?.audio_consent_id !== receipt.audio_consent_id) throw new Error("Recording permission changed. Nothing was uploaded.");
    if (!/^asn_[0-9a-f]{32}$/.test(session?.audio_session_id || "") || session.audio_consent_id !== receipt.audio_consent_id
        || session.game_id !== gameId || session.participant_id !== receipt.participant_id || !(Date.parse(session.expires_at) > Date.now())) {
        throw new Error("Confirm recording storage for this game session first.");
    }
    if (!blob?.size || blob.size > 8 * 1024 * 1024) throw new Error("The recording must be at most 8 MiB.");
    if (!uploadKeys.has(blob)) uploadKeys.set(blob, "audio_" + globalThis.crypto.randomUUID());
    return requestPrivateAudio("recordings", { method: "POST", body: blob, signal, idempotencyKey: uploadKeys.get(blob),
        headers: { "Content-Type": blob.type || "application/octet-stream", "X-Audio-Consent-ID": receipt.audio_consent_id,
            "X-Game-ID": gameId, "X-Audio-Session-ID": session.audio_session_id } });
};
